import analyticstracker from "@jmc/analyticstracker";
import { convertSecondsToTimeString } from "@jmc/utils/utils/date-to-string";
import { useEventListener } from "@jmc/utils/hooks/useEventListener";
import { useOnScreen } from "@jmc/utils/hooks/useOnScreen";
import { useCookieInteraction } from "@jmc/core/src/hooks/useCookieInteraction/index";
import { EventTypes } from "@jmc/core/src/types/EventTypes";
import { convert } from "../../utils/convert/convert";
import React, { useRef, useState, useEffect } from "react";
import dayjs from "dayjs";
import { MediaStatus } from "@jmc/solid-design-system/src/components/organisms/VideoPlayer/MediaStatus";

export type PlayerTypes = "brightcove" | "qumu" | "youtube" | "OLS" | "openmeet" | "audioplayer";

export interface PropTypes {
    children: JSX.Element;
    id: string;
    player: PlayerTypes;
    type: string;
    category?: string;
    eventstatus?: string;
    eventpurpose?: string;
    name: string;
    placement: string;
    position: number;
    targeturl?: string;
    autoplay?: boolean;
    mediasource?: string;
    thresholdvalue?: number;
    mediaClickEnabled?: boolean;
    liveStatusCurrent?: string;
    liveStatusUpdate?: any;
    minutesWatchedIntervalCurrent?: any;
    setMinutesWatchedIntervalUpdate?: any;
    fillParentContainerVertically?: boolean;
}

export enum LiveStatus {
    PRE_EVENT = "PRE_EVENT",
    LIVE_INITIALIZING = "LIVE_INITIALIZING",
    LIVE = "LIVE",
    LIVE_PAUSED = "LIVE_PAUSED",
    POST_EVENT = "POST_EVENT",
}

export const MediaImpression: React.FunctionComponent<PropTypes> = (props: PropTypes) => {
    const [playerCurrentTime, setPlayerCurrentTime] = useState(0);
    const [playerStartTime, setPlayerStartTime] = useState("00:00");
    const [playerPauseTime, setPlayerPauseTime] = useState("00:00");
    const [playerLiveStartTime, setPlayerLiveStartTime] = useState(null);
    const [playerLiveEndTime, setPlayerLiveEndTime] = useState(null);
    const [autoStop, setAutoStop] = useState(false);
    const [stop] = useState(false);
    const [length, setLength] = useState(0);
    const [cookieAccept, setCookieAccept] = useState(false);
    const [threshold, setThreshold] = useState(0);
    const [status, setStatus] = useState(MediaStatus.LOADED);
    const [liveStatus, setLiveStatus] = useState(LiveStatus.PRE_EVENT);
    const [minutesWatchedInterval, setMinutesWatchedInterval] = useState(null);
    const [isLivestream, setIsLivestream] = useState(false);
    const { interacted } = useCookieInteraction();
    const [impressionTracked, setImpressionTracked] = useState(false);

    const {
        children,
        id,
        player,
        type,
        category = null,
        name,
        placement,
        position,
        targeturl = null,
        eventstatus = null,
        eventpurpose = null,
        autoplay = false,
        mediasource = null,
        thresholdvalue = 0,
        mediaClickEnabled = true,
        liveStatusCurrent,
        liveStatusUpdate,
        minutesWatchedIntervalCurrent,
        setMinutesWatchedIntervalUpdate,
        fillParentContainerVertically = false,
    } = props;
    const cardContainer = useRef<HTMLDivElement>(null);
    const inViewport = useOnScreen(cardContainer, thresholdvalue);

    useEffect(() => {
        if (
            (player?.toLowerCase() === "ols" && inViewport && interacted) ||
            (player?.toLowerCase() === "youtube" && inViewport && interacted) ||
            (player?.toLowerCase() !== "ols" && length > 0 && inViewport && interacted) ||
            (player?.toLowerCase() === "brightcove" && inViewport && interacted) ||
            (player?.toLowerCase() === "openmeet" && inViewport && interacted)
        ) {
            setTimeout(() => {
                if (!impressionTracked) {
                    setImpressionTracked(true);

                    analyticstracker().trackImpression(EventTypes.MEDIA_IMPRESSION, {
                        info: { targeturl: targeturl },
                    });
                }
            }, 100);
        }
    }, [length, inViewport, interacted]);

    useEventListener(
        "click",
        (e: Event) => {
            if (status === MediaStatus.LOADED && mediaClickEnabled)
                analyticstracker().trackInteraction(e, {
                    changeEvent: EventTypes.MEDIA_CLICK,
                });
        },
        cardContainer,
    );

    useEffect(() => {
        if (autoplay) {
            analyticstracker().trackInteraction(
                { currentTarget: cardContainer.current },
                { changeEvent: EventTypes.MEDIA_PLAY },
            );
        }
    }, [length]);

    useEffect(() => {
        if (status === MediaStatus.PLAY && !autoStop && length > 0) {
            analyticstracker().trackInteraction(
                { currentTarget: cardContainer.current },
                { changeEvent: EventTypes.MEDIA_PLAY },
            );
        }
        if (
            status === MediaStatus.PAUSE &&
            !autoStop &&
            convertSecondsToTimeString(length) !== convert(parseInt(playerPauseTime))
        ) {
            analyticstracker().trackInteraction(
                { currentTarget: cardContainer.current },
                { changeEvent: EventTypes.MEDIA_PAUSE },
            );
        }
    }, [status, length]);

    useEffect(() => {
        if (liveStatus === LiveStatus.LIVE && liveStatusCurrent != LiveStatus.LIVE) {
            // Updating the liveStatus value in the parent to avoid multiple MEDIA_LIVE event.
            liveStatusUpdate && liveStatusUpdate(LiveStatus.LIVE);
            analyticstracker().trackInteraction(
                { currentTarget: cardContainer.current },
                { changeEvent: EventTypes.MEDIA_LIVE },
            );
            const minutesWatchedIntervalId = setInterval(() => {
                analyticstracker().trackInteraction(
                    { currentTarget: cardContainer.current },
                    {
                        changeEvent: EventTypes.MEDIA_MINUTES_WATCHED,
                    },
                );
            }, 300000);
            setMinutesWatchedInterval(minutesWatchedIntervalId);
            setMinutesWatchedIntervalUpdate && setMinutesWatchedIntervalUpdate(minutesWatchedIntervalId);
            //setMinutesWatchedIntervalUpdate is updated in the parent since setMinutesWatchedInterval was trying to update null instead of minutesWatchedIntervalId to avoid MEDIA_MINUTES_WATCHED after the event has ended.
        }
        if (liveStatus === LiveStatus.LIVE_PAUSED) {
            clearInterval(minutesWatchedInterval);
            clearInterval(minutesWatchedIntervalCurrent);
            // Clearing the interval of the minutesWatchedInterval from the parent.
        }
        if (liveStatus === LiveStatus.POST_EVENT) {
            analyticstracker().trackInteraction(
                { currentTarget: cardContainer.current },
                { changeEvent: EventTypes.MEDIA_LIVE_END },
            );
            clearInterval(minutesWatchedInterval);
            clearInterval(minutesWatchedIntervalCurrent);
            // Clearing the interval of the minutesWatchedInterval from the parent.
        }
    }, [liveStatus]);

    useEffect(() => {
        if (!isLivestream) {
            const twentyfivePct = length * 0.25;
            const fiftyPct = length * 0.5;
            const seventyfivePct = length * 0.75;
            const hundredPct = length - 5;

            if (playerCurrentTime > twentyfivePct && threshold < 0.25 && length > 0) {
                setThreshold(0.25);
                analyticstracker().trackInteraction(
                    { currentTarget: cardContainer.current },
                    { changeEvent: EventTypes.MEDIA_25PCT },
                );
            }

            if (playerCurrentTime > fiftyPct && threshold < 0.5 && length > 0) {
                setThreshold(0.5);
                analyticstracker().trackInteraction(
                    { currentTarget: cardContainer.current },
                    { changeEvent: EventTypes.MEDIA_50PCT },
                );
            }

            if (playerCurrentTime > seventyfivePct && threshold < 0.75 && length > 0) {
                setThreshold(0.75);
                analyticstracker().trackInteraction(
                    { currentTarget: cardContainer.current },
                    { changeEvent: EventTypes.MEDIA_75PCT },
                );
            }

            if (playerCurrentTime >= hundredPct && threshold < 1 && length > 0) {
                setThreshold(1);
                analyticstracker().trackInteraction(
                    { currentTarget: cardContainer.current },
                    { changeEvent: EventTypes.MEDIA_COMPLETE },
                );
            }
        }
    }, [playerCurrentTime]);

    const mediastop = (mediaStopStatus: MediaStatus | boolean, currentTime: number | string): void => {
        // For audios, mediaStopStatus comes as a boolean, for videos, it's a string.
        let currentStatus = mediaStopStatus as MediaStatus;
        if (typeof mediaStopStatus === "boolean") {
            currentStatus = mediaStopStatus ? MediaStatus.PLAY : MediaStatus.PAUSE;
        }

        // For some players, currenTime comes as a number, others as a string.
        let standardTime = currentTime as string;
        if (typeof currentTime === "number") {
            standardTime = convert(parseInt(currentTime.toString()));
        }

        if (currentStatus === MediaStatus.PLAY) {
            const liveTime = playerLiveStartTime ? convert(dayjs().diff(playerLiveStartTime, "second")) : convert(0);
            setPlayerStartTime(isLivestream ? liveTime : standardTime);
        } else if (currentStatus === MediaStatus.PAUSE) {
            setPlayerPauseTime(standardTime);
        }

        setStatus(currentStatus);
        setAutoStop(false);
    };

    const medialive = (
        mediaLiveStatus: LiveStatus,
        liveStartTime: number | string,
        liveEndTime: number | string,
    ): void => {
        setIsLivestream(true);
        setPlayerLiveStartTime(liveStartTime);
        setPlayerLiveEndTime(liveEndTime);
        setLiveStatus(mediaLiveStatus);
        setAutoStop(false);
    };

    const getTimePlay = (): string => {
        if (isLivestream) {
            return "unset";
        } else {
            return playerStartTime;
        }
    };

    const getTimePause = (): string => {
        if (isLivestream) {
            return "unset";
        } else {
            return playerPauseTime;
        }
    };

    const getTimeStamp = (): string => {
        if (isLivestream) {
            if (liveStatus === LiveStatus.POST_EVENT) {
                return convert(dayjs(playerLiveEndTime).diff(playerLiveStartTime, "second"));
            }
            return convert(dayjs().diff(playerLiveStartTime, "second"));
        }
        return null;
    };

    return (
        <div
            role="none"
            data-tracking-event={EventTypes.MEDIA_IMPRESSION}
            data-tracking-info={JSON.stringify({
                type,
                id,
                player,
                timestop: stop ? convertSecondsToTimeString(playerCurrentTime) : null,
                timeplay: !stop ? getTimePlay() : null,
                timepause: !stop ? getTimePause() : null,
                timestamp: !stop ? getTimeStamp() : null,
                length: !isLivestream ? convertSecondsToTimeString(length) : "unset",
                cookieAccept: type === "video" || type === "audio" ? cookieAccept : null,
                autoplay,
                mediasource,
                category,
                eventstatus,
                eventpurpose,
                name,
                placement,
                position,
                targeturl: targeturl || "unset",
            })}
            style={{ width: "100%", ...(fillParentContainerVertically ? { height: "100%" } : {}) }}
            ref={cardContainer}
        >
            {React.cloneElement(children, {
                mediastop: mediastop,
                medialive: medialive,
                getLength: setLength,
                cookieAccept: setCookieAccept,
                getCurrentTime: setPlayerCurrentTime,
            })}
        </div>
    );
};

export default MediaImpression;
